// WHAT: Session Cards Component (Molecule)
// WHY:  Displays clickable cards for starting morning and evening sessions
// DOMAIN: Productivity → Miracle Flow
// DEPENDENCIES: UI Card, Button, Stack, Heading, Badge components, Lucide icons
// RISKS:
//   - NOTE: Cards have both onClick and button onClick (may cause double navigation)
//   - NOTE: Session step counts are hardcoded (6 for morning, 4 for evening)
//   - NOTE: Buttons disabled when hasActiveSession (no indication why)

import { Sun, Moon, Sparkles, ArrowRight } from "lucide-react";
import React from "react";

import { Card, Button, Stack, Heading, Badge } from "@/components/ui";
import { cn } from "@/shared/utils";

interface SessionCardsProperties {
  hasActiveSession: boolean;
  onNavigate: (route: string) => void;
}

/**
 * WHAT:
 *   Component displaying morning and evening session cards with navigation.
 *
 * WHY:
 *   Provides entry points for starting Miracle Flow sessions with visual
 *   distinction between morning and evening routines.
 *
 * PROPS:
 *   - onNavigate: (route: string) => void — Callback when session card is clicked
 *   - hasActiveSession: boolean — Whether a session is currently active
 *
 * BEHAVIOR:
 *   - Displays two cards: Morning Session (6 steps) and Evening Session (4 steps)
 *   - Cards are clickable and trigger onNavigate callback
 *   - Buttons also trigger navigation (with stopPropagation to prevent double call)
 *   - Buttons disabled when hasActiveSession is true
 *   - Uses different color schemes: blue gradient for morning, orange for evening
 *   - Shows step counts and descriptions for each session type
 *   - Cards have hover effects (border, shadow, translate)
 *
 * RISKS:
 *   - NOTE: Step counts are hardcoded (may not match actual session structure)
 *   - NOTE: Both card and button trigger navigation (redundant but handled with stopPropagation)
 *   - NOTE: Disabled state doesn't show tooltip explaining why
 *
 * @param root0
 *
 * @param root0.onNavigate
 *
 * @param root0.hasActiveSession
 *
 * @returns JSX.Element
 *
 */
export const SessionCards: React.FC<SessionCardsProperties> = ({
  hasActiveSession,
  onNavigate,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Morning Session */}
      <Card
        className={cn(
          "relative overflow-hidden bg-[#1E293B] border-[#334155]",
          "hover:border-tracq-bright-blue/50 cursor-pointer transition-all duration-300",
          "hover:shadow-tracq-card hover:-translate-y-1",
        )}
        onClick={() => {
          onNavigate("morning-session");
        }}
        padding="lg"
        variant="default"
      >
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-tracq-deep-blue/10 via-transparent to-tracq-bright-blue/10 opacity-0 hover:opacity-100 transition-opacity duration-300" />

        <Stack className="relative z-10" direction="col" gap={4}>
          <Stack align="center" direction="row" gap={4}>
            <div className="flex items-center justify-center w-16 h-16 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
              <Sun className="w-8 h-8 text-white" />
            </div>
            <Stack className="flex-1" direction="col" gap={2}>
              <Heading className="text-[#F1F5F9] font-bold" level="h3" size="lg">
                Morning Session
              </Heading>
              <Stack align="center" className="flex-wrap" direction="row" gap={2}>
                <Badge
                  className="bg-tracq-bright-blue/20 text-tracq-bright-blue border-tracq-bright-blue/30"
                  size="sm"
                  variant="info"
                >
                  <Sparkles className="w-3 h-3 mr-1" />6 Steps
                </Badge>
                <p className="text-[#94A3B8] text-sm">
                  Meditation, Affirmations, Visualization, Exercise, Reading, Scribing
                </p>
              </Stack>
            </Stack>
          </Stack>

          <Button
            className="w-full bg-gradient-to-r from-tracq-deep-blue to-tracq-bright-blue hover:from-tracq-deep-blue/90 hover:to-tracq-bright-blue/90 text-white font-semibold"
            disabled={hasActiveSession}
            onClick={(e) => {
              e.stopPropagation();
              onNavigate("morning-session");
            }}
            size="lg"
            variant="primary"
          >
            Start Morning Session
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Stack>
      </Card>

      {/* Evening Session */}
      <Card
        className={cn(
          "relative overflow-hidden bg-[#1E293B] border-[#334155]",
          "hover:border-tracq-bright-blue/50 cursor-pointer transition-all duration-300",
          "hover:shadow-tracq-card hover:-translate-y-1",
        )}
        onClick={() => {
          onNavigate("evening-session");
        }}
        padding="lg"
        variant="default"
      >
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-tracq-deep-orange/10 via-transparent to-tracq-light-orange/10 opacity-0 hover:opacity-100 transition-opacity duration-300" />

        <Stack className="relative z-10" direction="col" gap={4}>
          <Stack align="center" direction="row" gap={4}>
            <div className="flex items-center justify-center w-16 h-16 rounded-tracq bg-gradient-to-br from-tracq-deep-orange to-tracq-light-orange shadow-tracq-card">
              <Moon className="w-8 h-8 text-white" />
            </div>
            <Stack className="flex-1" direction="col" gap={2}>
              <Heading className="text-[#F1F5F9] font-bold" level="h3" size="lg">
                Evening Session
              </Heading>
              <Stack align="center" className="flex-wrap" direction="row" gap={2}>
                <Badge
                  className="bg-tracq-light-orange/20 text-tracq-light-orange border-tracq-light-orange/30"
                  size="sm"
                  variant="info"
                >
                  <Sparkles className="w-3 h-3 mr-1" />4 Steps
                </Badge>
                <p className="text-[#94A3B8] text-sm">
                  Reflection, Affirmations, Visualization, Meditation
                </p>
              </Stack>
            </Stack>
          </Stack>

          <Button
            className="w-full bg-gradient-to-r from-tracq-deep-orange to-tracq-light-orange hover:from-tracq-deep-orange/90 hover:to-tracq-light-orange/90 text-white font-semibold"
            disabled={hasActiveSession}
            onClick={(e) => {
              e.stopPropagation();
              onNavigate("evening-session");
            }}
            size="lg"
            variant="primary"
          >
            Start Evening Session
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Stack>
      </Card>
    </div>
  );
};
